import {
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  styled,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { ErrorFormat } from "utils/ErrorUtils";

interface Props {
  setAcceptedFiles: (files: File[]) => void;
  errors: ErrorFormat[];
  resetErrors: () => void;
  maxUploadSizeMB: number;
}

export function UploadDropZone(props: Props) {
  const { setAcceptedFiles, resetErrors, errors } = props;
  const { t } = useTranslation();
  const {
    acceptedFiles,
    fileRejections,
    isFocused,
    isDragAccept,
    isDragReject,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: {
      "application/zip": [".zip"],
    },
    maxFiles: 1,
    onDropAccepted: (files) => {
      setAcceptedFiles(files);
    },
    onDrop: () => {
      resetErrors();
    },
  });

  const dropzoneClasses = [
    "dropzone",
    isFocused && "focused",
    isDragAccept && "accepted",
    isDragReject && "rejected",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Styles className="container">
      <div {...getRootProps()} className={dropzoneClasses}>
        <input {...getInputProps()} />
        <p>{t("label.dropMeasureFileHere")}</p>
      </div>
      {acceptedFiles.length > 0 && (
        <Alert security="success" variant="outlined">
          <AlertTitle>{t("label.acceptedFiles")}</AlertTitle>
          <List disablePadding>
            {acceptedFiles.map((file, index) => (
              <ListItem key={`${file.name}-${index}`}>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
      {(fileRejections.length > 0 || errors.length > 0) && (
        <Alert severity="error">
          <AlertTitle>{t("label.errors")}</AlertTitle>
          <List disablePadding>
            {fileRejections.map(({ file, errors }, index) => (
              <ListItem disableGutters key={`${file.name}-${index}`}>
                <ListItemText
                  primary={file.name}
                  secondary={errors.map((error) => error.code)}
                />
              </ListItem>
            ))}
            {errors.map((error, index) => (
              <ListItem disableGutters key={`${error.title}-${index}`}>
                <ListItemText primary={error.message} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </Styles>
  );
}

const Styles = styled("section")`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  .dropzone {
    flex: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    border-width: 2px;
    border-radius: 2px;
    border-color: ${({ theme }) => theme.palette.grey[400]};
    border-style: dashed;
    background-color: ${({ theme }) => theme.palette.grey[100]};
    color: ${({ theme }) => theme.palette.grey[500]};
    outline: none;
    transition: border 0.24s ease-in-out;
    cursor: pointer;
    justify-content: center;
    min-height: ${({ theme }) => theme.spacing(20)};
    &.focused {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
    &.accepted {
      border-color: ${({ theme }) => theme.palette.success.main};
    }
    &.rejected {
      border-color: ${({ theme }) => theme.palette.error.main};
    }
  }
`;
